import * as React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';

/** Material UI Packages. */
import {
    Box,
    Drawer,
    Tabs,
    Tab,
    Badge,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Stack,
    Paper,
    Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setTabValueData } from 'shared/store/slices/tabValueSlice';
import { updateFilterView, resetFormData } from 'shared/store/slices/filterViewSlice';
import { setProgessValueData } from 'shared/store/slices/progressValueSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { setUpdateTodo } from 'shared/store/slices/todoListSlice';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import ThemeButton from 'shared/ui/Button';
import TodoReminder from './TodoReminder';
import todoSortingHandler from 'shared/lib/todoSortingHandler';
import { todoFilterHandler } from 'shared/lib/todoFilterHandler';
import { getOperationToday } from 'shared/lib/dateFormatter';
import { createSocketData, deleteSocketData } from 'shared/lib/fetchSocket';

import TodoCards from './TodoCards';
import { todoSections } from 'data/dashboardConfig';
import ReminderCards from './ReminderCards';
import TodoSearchBar from './TodoSearchBar';

/** Fields on a todo item that are matched against the panel's search query. */
const SEARCHABLE_TODO_FIELDS = ['header', 'description', 'il_opus_reference_id', 'client', 'shipper', 'consignee'];

/** Check whether a todo item matches the given (already-normalized) search query. */
const matchesSearchQuery = (item, normalizedQuery) =>
    SEARCHABLE_TODO_FIELDS.some((field) =>
        String(item[field] ?? '')
            .toLowerCase()
            .includes(normalizedQuery)
    );

/** How long a card keeps its "just changed" highlight after a live update lands in the panel. */
export const LIVE_UPDATE_HIGHLIGHT_MS = 2500;

/** Fields a card renders from the todo row. When any of these change on an incoming update the card
 * gets a short highlight so the reader can spot what moved. Per-user read state is left out: toggling
 * read/unread is the reader's own action, not news.
 */
const LIVE_UPDATE_FIELDS = [
    'header',
    'description',
    'il_opus_reference_id',
    'client',
    'shipper',
    'consignee',
    'intensity',
    'intensity_date',
    'status',
    'step',
    'step_value',
];

/** Build a comparable snapshot of the fields a card displays. */
const liveUpdateSignature = (item) => JSON.stringify(LIVE_UPDATE_FIELDS.map((field) => item[field] ?? null));

/** Identity of a card's todo row, or null for synthetic rows without an id. */
const todoCardIdentity = (item) => (item.id != null ? `${item.todo_key}-${item.id}` : null);

/** Key a card by its todo row so React keeps the same DOM node for the same card across live updates,
 * instead of re-pairing cards by position whenever one is inserted, removed, or re-sorted above it.
 * Rows without an id (synthetic cards) fall back to their position.
 */
const todoCardKey = (item, index) => todoCardIdentity(item) ?? `pos-${index}`;

/** Define the styled badge for todo panel. */
const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 1,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 2px',
        fontSize: '9px',
    },
}));

/** Placeholder shown in place of a TodoCards item while it's being filtered/sorted. */
const TodoCardSkeleton = () => (
    <Paper data-testid="todo-card-skeleton" sx={{ width: '100%', minHeight: '78px', p: 0.5, px: 1.5 }}>
        <Skeleton animation="wave" width="55%" height={16} />
        <Skeleton animation="wave" width="85%" height={12} />
        <Skeleton animation="wave" width="100%" height={12} />
        <Skeleton animation="wave" width="100%" height={5} sx={{ mt: 0.5, borderRadius: 5 }} />
    </Paper>
);

/** Create a resusable component. */
const TabWithBadge = ({ label, badgeContent }) => (
    <StyledBadge badgeContent={badgeContent} color="error">
        {label}
    </StyledBadge>
);

/** Reusable tab style to prevent repeated code. */
const tabStyle = {
    ...customized_style.itemTab,
    bgcolor: 'white',
    minWidth: 70,
    textTransform: 'none',
    fontSize: 11,
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            style={{ backgroundColor: '#f5f5f5' }}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

/** Create an array of tab data to map over. */
const defaultTabData = [
    { label: 'ALL', badgeContent: 0, value: 0, type: 'regular', width: '65px' },
    { label: 'URGENT', badgeContent: 0, value: 1, type: 'regular', width: '70px' },
    { label: 'NORMAL', badgeContent: 0, value: 2, type: 'regular', width: '75px' },
    { label: 'COMPLETE', badgeContent: 0, value: 3, type: 'regular', width: '80px' },
    { label: 'ALERT', badgeContent: 0, value: 4, type: 'reminders', width: '60px' },
];

export default function TodoPanel(props) {
    /** Get props from Shipments.js. */
    const { reminderList, setReminderList } = props;
    /** Get props from TodoFab.js.  */
    const { isDrawerOpen, setIsDrawerOpen } = props;

    const theme = useTheme();
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    const rawTodoList = useSelector((state) => state.shipmentTodo.panel);

    /** Sorting todo list by priority. */
    const todoData = useMemo(() => todoSortingHandler(rawTodoList), [rawTodoList]);
    // Overdue cards must stay visible to a user logging in from Korea, where the
    // local date runs ahead of the operations day (LA) that the cards are kept on.
    const today = getOperationToday();

    /** State for tab value. */
    const [value, setValue] = useState(1);
    /** State to manage the current showing todo list. */
    const [todoList, setTodoList] = useState([]);
    /** Whether the todo filtering/sorting for the active tab is still being computed;
     * drives the skeleton shown in place of the accordions/cards below.
     */
    const [isFiltering, setIsFiltering] = useState(false);
    /** Holds the last computed sections so the skeleton branch doesn't need to recompute them. */
    const filterTodoRef = useRef([]);
    /** State to store the filtered todo list based on todo key. */
    const filterTodo = useMemo(() => {
        if (isFiltering) return filterTodoRef.current;
        const sections = todoSections(todoFilterHandler(todoList, today, 'shipments'));
        filterTodoRef.current = sections;
        return sections;
    }, [todoList, isFiltering]);
    /** State to manage custom reminder(alert) dialog. */
    const [reminderOpen, setReminderOpen] = useState(false);
    /** State to store quickFilter value. */
    const [filterValue, setFilterValue] = useState(null);
    /** State to store todo tabData. */
    const [tabData, setTabData] = useState([]);
    /** State to store the current search query for filtering todo cards. */
    const [searchQuery, setSearchQuery] = useState('');
    const normalizedQuery = searchQuery.trim().toLowerCase();

    /** Flat list of todo cards matching the search query, ignoring accordion grouping.
     * Each todo row belongs to exactly one section (a Disputing case_closure card goes to Disputes
     * only, OS-575). The identity check stays as a guard: two entries would share a key and React
     * would drop or duplicate one of them.
     */
    const searchedCards = useMemo(() => {
        if (!normalizedQuery) return [];
        const seen = new Set();
        return filterTodo
            .flatMap(({ data }) => data.filter((item) => matchesSearchQuery(item, normalizedQuery)))
            .filter((item) => {
                const identity = todoCardIdentity(item);
                if (identity === null) return true;
                if (seen.has(identity)) return false;
                seen.add(identity);
                return true;
            });
    }, [filterTodo, normalizedQuery]);
    /** Filter todoData down to the given tab value. */
    const getFilteredDataByTab = (tabValue) => {
        switch (tabValue) {
            case 0: // All
                return todoData;
            case 1: // Urgent
                return todoData.filter((item) => item.status === false && item.intensity === true);
            case 2: // Normal
                return todoData.filter((item) => item.status === false && item.intensity === false);
            case 3: // Complete
                return todoData.filter((item) => item.status === true);
            default:
                return todoData;
        }
    };

    /** Show the skeleton only when the whole card set is swapped out: the drawer opening or a tab
     * switch. Live todo updates over the websocket (a teammate saving a shipment) must not take this
     * path. They used to blank the panel to skeletons on every emit, which read as flicker and also
     * reset the expanded accordions and scroll position each time.
     */
    useEffect(() => {
        if (isDrawerOpen) setIsFiltering(true);
    }, [isDrawerOpen, value]);

    /** Keep todoList in sync with the active tab whenever the drawer opens, the tab changes, or
     * todoData updates (a live update, or marking a todo as read). Data-only updates re-derive the
     * list in place and leave the mounted cards where they are.
     */
    useEffect(() => {
        if (isDrawerOpen) setTodoList(getFilteredDataByTab(value));
    }, [isDrawerOpen, todoData, value]);

    /** Ids of cards changed by a recent live update, highlighted until their timer clears. */
    const [highlightedIds, setHighlightedIds] = useState(() => new Set());
    /** Last seen card signatures by todo id; null until the first list has been seen. */
    const liveSignaturesRef = useRef(null);
    /** Pending highlight-clear timers, cancelled on unmount. */
    const highlightTimersRef = useRef([]);

    /** Diff each incoming todoData against the last one and flag the cards whose content changed.
     * The first (seed) list and the empty→loaded transition are initial loads, not live updates.
     */
    useEffect(() => {
        const nextSignatures = new Map();
        todoData.forEach((item) => {
            if (item.id != null) nextSignatures.set(item.id, liveUpdateSignature(item));
        });
        const prevSignatures = liveSignaturesRef.current;
        liveSignaturesRef.current = nextSignatures;
        if (!prevSignatures || prevSignatures.size === 0) return;

        const changedIds = [];
        nextSignatures.forEach((signature, id) => {
            if (prevSignatures.get(id) !== signature) changedIds.push(id);
        });
        if (changedIds.length === 0) return;

        setHighlightedIds((current) => new Set([...current, ...changedIds]));
        const timer = setTimeout(() => {
            setHighlightedIds((current) => {
                const next = new Set(current);
                changedIds.forEach((id) => next.delete(id));
                return next;
            });
            highlightTimersRef.current = highlightTimersRef.current.filter((pending) => pending !== timer);
        }, LIVE_UPDATE_HIGHLIGHT_MS);
        highlightTimersRef.current.push(timer);
    }, [todoData]);

    /** Clear any highlight timers still pending when the panel unmounts. */
    useEffect(() => () => highlightTimersRef.current.forEach(clearTimeout), []);

    /** Once the new todoList has been committed (and the skeleton above has had a chance to
     * paint), run the actual filtering/sorting on the next frame and swap the skeleton for
     * the real cards.
     */
    useEffect(() => {
        if (!isDrawerOpen) return undefined;
        const frameId = requestAnimationFrame(() => setIsFiltering(false));
        return () => cancelAnimationFrame(frameId);
    }, [todoList, isDrawerOpen]);

    /** Calculate todo count based on todo category and update tabData. */
    useEffect(() => {
        // Count cards the same way they're rendered (via todoFilterHandler/todoSections' todo_key
        // and step bucketing), so a tab's badge matches the cards actually shown in its accordions.
        const countDisplayedTodos = (list) =>
            todoSections(todoFilterHandler(list, today, 'shipments')).reduce(
                (total, { data }) => total + data.length,
                0
            );

        const counts = {
            0: countDisplayedTodos(todoData),
            1: countDisplayedTodos(todoData.filter((item) => item.status === false && item.intensity === true)),
            2: countDisplayedTodos(todoData.filter((item) => item.status === false && item.intensity === false)),
            3: countDisplayedTodos(todoData.filter((item) => item.status === true)),
        };

        // Update badgeContent based on defaultTabData.
        const updatedTabs = defaultTabData.map((tab) => {
            if (tab.label === 'ALERT') {
                return { ...tab, badgeContent: reminderList.length };
            } else if (counts.hasOwnProperty(tab.value)) {
                return { ...tab, badgeContent: counts[tab.value] };
            }
            return tab; // Keep other tabs unchanged.
        });

        setTabData(updatedTabs);
    }, [todoData, reminderList]);

    /** Hook to update filterView value and close the dialog when the user wants to navigate to a specific shipment. */
    useEffect(() => {
        if (filterValue) {
            dispatch(updateFilterView(filterValue));
            setIsDrawerOpen(false);
        }
    }, [filterValue]);

    /** Handle to close a todo panel drawer. */
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setValue(1);
        setSearchQuery('');
    };

    /** Handle to open a custom reminder dialog. */
    const handleReminderOpen = () => {
        setReminderOpen(true);
    };

    /** Handle to show the clicked shipment data in datagrid. */
    const handleMove = (value) => {
        const updatedTabValue = value.shipment_type;
        const updatedProgressValue = value.attr_value == 'Disputing' ? 'dispute' : 'inprogress';
        if (value.il_opus_reference_id) {
            if (typeof value.id === 'number' && !value.is_read) {
                createSocketData(dispatch, '', 'row', 'il_todo_read', null, { il_shipment_todo_id: value.id }, () => {
                    dispatch(setUpdateTodo([{ ...value, is_read: true }]));
                });
            }
            const payload = {
                filterModel: { items: [], quickFilterValues: [value.il_opus_reference_id] },
                id: '',
                name: '',
            };
            setFilterValue(payload);
            dispatch(setTabValueData(updatedTabValue));
            dispatch(setProgessValueData(updatedProgressValue));
            dispatch(resetFormData());
            setIsDrawerOpen(false);
            setValue(1);
        } else {
            dispatch(setSnackbar({ open: true, message: 'Opus reference is empty!', severity: 'error' }));
        }
    };

    /** Handle to toggle a todo's read state (server-persisted per-user via `il_todo_read`).
     * Card stays in the panel either way; only its dimmed/active CSS state changes.
     */
    const handleToggleRead = (item) => {
        const nextIsRead = !item.is_read;
        const emitReadState = nextIsRead ? createSocketData : deleteSocketData;
        emitReadState(dispatch, '', 'row', 'il_todo_read', null, { il_shipment_todo_id: item.id }, () => {
            dispatch(setUpdateTodo([{ ...item, is_read: nextIsRead }]));
        });
    };

    /** Handle to change a tab. The todoList sync effect re-derives the filtered data for the new tab. */
    const handleChange = (event, newValue) => {
        setValue(newValue);
        setSearchQuery('');
    };

    const handleReminderDelete = (id) => {
        if (window.confirm('Do you want to delete this reminder?')) {
            deleteSocketData(dispatch, '', 'row', 'il_alert', null, id, () => {
                dispatch(setSnackbar({ open: true, message: 'Successfully deleted', severity: 'success' }));
                setReminderList((prev) => prev.filter((item) => item.id !== id));
            });
        }
    };

    /** Render a single todo card, shared between the accordion view and the search results view. */
    const renderTodoCard = (item, index) => (
        <TodoCards
            key={todoCardKey(item, index)}
            isRecentlyUpdated={item.id != null && highlightedIds.has(item.id)}
            header={item.header}
            description={item.description}
            il_opus_reference_id={item.il_opus_reference_id}
            client={item.client}
            shipper={item.shipper}
            consignee={item.consignee}
            is_read={item.is_read}
            canMarkRead={typeof item.id === 'number'}
            onToggleRead={() => handleToggleRead(item)}
            intensity={item.intensity}
            intensity_date={item.intensity_date}
            status={!item.status}
            step={item.step}
            step_value={item.step_value}
            handleMove={() => handleMove(item)}
        />
    );

    return (
        <Drawer open={isDrawerOpen} onClose={handleDrawerClose} anchor="right">
            <Box
                sx={{ width: 350, pt: 6, bgcolor: '#f3f4f3', height: '100%', display: 'flex', flexDirection: 'column' }}
            >
                <Box sx={{ flexShrink: 0 }}>
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            p: 0,
                            m: 0,
                            height: '37px',
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            sx={{
                                '& .MuiButtonBase-root': { p: 0 },
                                '& .MuiTabs-scroller': { height: '36px' },
                            }}
                        >
                            {tabData.map((tab, index) => (
                                <Tab
                                    key={index}
                                    label={
                                        tab.badgeContent ? (
                                            <TabWithBadge label={tab.label} badgeContent={tab.badgeContent} />
                                        ) : (
                                            tab.label
                                        )
                                    }
                                    sx={{ ...tabStyle, minWidth: tab.width }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    {tabData.map((tab, index) => (
                        <TabPanel key={index} value={value} index={index} dir={theme.direction}>
                            {tab.type === 'regular' ? (
                                <Box>
                                    <Box sx={{ px: 1, pt: 1 }}>
                                        <TodoSearchBar value={searchQuery} onChange={setSearchQuery} />
                                    </Box>
                                    {isFiltering ? (
                                        <Box
                                            sx={{
                                                flexGrow: 1,
                                                p: 1,
                                                pt: 0.75,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.625,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {Array.from({ length: 5 }).map((_, index) => (
                                                <TodoCardSkeleton key={index} />
                                            ))}
                                        </Box>
                                    ) : normalizedQuery ? (
                                        <Box
                                            sx={{
                                                flexGrow: 1,
                                                p: 1,
                                                pt: 0.75,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.625,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {searchedCards.length > 0 ? (
                                                searchedCards.map((item, index) => renderTodoCard(item, index))
                                            ) : (
                                                <Typography
                                                    sx={{ fontSize: 11, color: '#7a7a7a', textAlign: 'center', mt: 2 }}
                                                >
                                                    No matching results.
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                flexGrow: 1,
                                                p: 1,
                                                pt: 0.4,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {filterTodo.map(({ id, data, config }) => (
                                                <Accordion
                                                    key={id}
                                                    sx={{
                                                        backgroundColor: 'transparent',
                                                        border: '1px solid #dfdfdf',
                                                        boxShadow: 'none',
                                                    }}
                                                    disabled={data.length <= 0 ? true : false}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        id={`panel-${id}-header`}
                                                        sx={{
                                                            minHeight: 30,
                                                            '& .MuiAccordionSummary-content.Mui-expanded': {
                                                                margin: 0,
                                                            },
                                                            '& .MuiAccordionSummary-content': {
                                                                margin: 0,
                                                            },
                                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                                                padding: 0,
                                                            },
                                                            '&.Mui-expanded': { minHeight: 30 },
                                                            backgroundColor: '#fff',
                                                            borderBottom: '1px solid #dfdfdf',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{ fontSize: 11, color: '#262626', fontWeight: 500 }}
                                                        >
                                                            {config.header}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                color: '#262626',
                                                                ml: 0.5,
                                                            }}
                                                        >
                                                            ({data.length})
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails
                                                        sx={{
                                                            padding: 0,
                                                            margin: 0,
                                                            height: '100%',
                                                            backgroundColor: 'transparent',
                                                            pt: 1,
                                                        }}
                                                    >
                                                        <Stack spacing={0.625}>
                                                            {data &&
                                                                data.map((item, index) => renderTodoCard(item, index))}
                                                        </Stack>
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            mt: 1,
                                            mr: 0.5,
                                        }}
                                    >
                                        <ThemeButton
                                            text="ADD"
                                            icon={<AddIcon />}
                                            width={'68px'}
                                            onClick={handleReminderOpen}
                                        />
                                    </Box>
                                    <Box sx={{ p: 1, gap: 1, display: 'flex', flexDirection: 'column' }}>
                                        {reminderList.map((item, index) => (
                                            <ReminderCards
                                                key={index}
                                                {...item}
                                                handleReminderDelete={() => handleReminderDelete(item.id)}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </TabPanel>
                    ))}
                </Box>
                <TodoReminder
                    reminderOpen={reminderOpen}
                    setReminderOpen={setReminderOpen}
                    setReminderList={setReminderList}
                />
            </Box>
        </Drawer>
    );
}
